import { Signal } from "@shared/types";
import { MARGIN_TYPE_TO_DISPLAY } from "@shared/common/constants";

const TERMS_SEPARATOR = " · ";

export type PhoneSignalFollowState = "cancel" | "update" | "following";

export type PhoneSignalSortKey = "recent" | "profit" | "potential" | "symbol";

export type PhoneSignalDetailKey = "opened" | "updated" | "closed";

export interface PhoneSignalProgress {
  entries: number;
  takeProfits: number;
}

export interface PhoneSignalDetail {
  key: PhoneSignalDetailKey;
  value: string;
}

export const getSignalProgress = (signal: Signal): PhoneSignalProgress => ({
  entries: clampShare(signal.graph_data?.entry_progress),
  takeProfits: clampShare(signal.graph_data?.take_profit_progress),
});

export const isSignalWaitingForEntry = (signal: Signal) =>
  signal.is_open && !getSignalProgress(signal).entries;

export const getSignalProfit = (signal: Signal) =>
  Number(signal.graph_data?.profit) || 0;

export const getSignalPotentialProfit = (signal: Signal) => {
  const ratio = Number(signal.signal_extra_data?.["Potential Profit Ratio"]);
  return isFinite(ratio) && ratio > 0 ? ratio * 100 : undefined;
};

export const getSignalRiskReward = (signal: Signal) =>
  signal.signal_extra_data?.["Risk/Reward"] || undefined;

export const getSignalPositionTerms = (
  signal: Signal,
  translate: (name: string) => string = (name) => name
) => {
  const leverage = signal.signal_extra_data?.["Leverage"];
  if (!leverage) return undefined;
  const marginType = signal.signal_extra_data?.["Margin Type"];
  const margin = marginType && MARGIN_TYPE_TO_DISPLAY[marginType];
  return [`${leverage}x`, margin && translate(margin)]
    .filter(Boolean)
    .join(TERMS_SEPARATOR);
};

export const getSignalFollowState = (
  signal: Signal
): PhoneSignalFollowState | undefined => {
  if (needsCancel(signal)) return "cancel";
  if (needsUpdate(signal)) return "update";
  return isFollowed(signal) ? "following" : undefined;
};

export const getSignalFollowNote = (
  signal: Signal
): PhoneSignalFollowState | undefined => {
  if (!signal.is_open) return needsCancel(signal) ? "cancel" : undefined;
  if (needsUpdate(signal)) return "update";
  return isFollowed(signal) ? "following" : undefined;
};

const needsCancel = (signal: Signal) =>
  !!(signal.requires_cancel?.length || signal.bot_requires_cancel?.length);

const needsUpdate = (signal: Signal) =>
  !!(signal.requires_update?.length || signal.bot_requires_update?.length);

const isFollowed = (signal: Signal) =>
  !!(signal.trade_exists?.length || signal.bot_trade_exists?.length);

export const getSignalExchanges = (signal: Signal) => {
  const listed =
    signal.signal_extra_data?.["Exchanges"]
      ?.split(",")
      .map((exchange) => exchange.trim())
      .filter(Boolean) ?? [];
  return [
    ...new Set(
      [signal.exchange, ...listed].filter(
        (exchange): exchange is string => !!exchange
      )
    ),
  ];
};

export const getSignalDetails = (
  signal: Signal,
  formatDate: (timestamp: number) => string
): PhoneSignalDetail[] => {
  const opened = signal.open_timestamp;
  const latest = signal.relevant_time;
  const details: PhoneSignalDetail[] = [];
  if (opened) details.push({ key: "opened", value: formatDate(opened) });
  if (latest && latest !== opened)
    details.push({
      key: signal.is_open ? "updated" : "closed",
      value: formatDate(latest),
    });
  return details;
};

export const PHONE_SIGNAL_SORTERS: Record<
  PhoneSignalSortKey,
  (first: Signal, second: Signal) => number
> = {
  recent: (first, second) =>
    (second.relevant_time || 0) - (first.relevant_time || 0),
  profit: (first, second) => getSignalProfit(second) - getSignalProfit(first),
  potential: (first, second) =>
    (getSignalPotentialProfit(second) ?? 0) -
    (getSignalPotentialProfit(first) ?? 0),
  symbol: (first, second) => first.symbol.localeCompare(second.symbol),
};

const clampShare = (value: unknown) => {
  const share = Number(value);
  return isFinite(share) ? Math.min(Math.max(share, 0), 1) : 0;
};
